package link.erpango.quip.domain.noteimport

import link.erpango.quip.domain.extraction.ExtractionConfidence

/**
 * Merges the offline parser draft with the AI draft into one list for review.
 *
 * Vehicles are paired 1:1 (same VIN, else same plate, else same brand + model). Within a
 * pair each field is merged on its own:
 * - only one side has it -> that value;
 * - both agree (case/accents/punctuation aside, or one contains the other as in "10W-40" vs
 *   "Repsol Helix 10W-40") -> the more complete value, source BOTH, confidence HIGH;
 * - they disagree -> the higher confidence wins and the other value becomes the
 *   [SuggestedField.alternative] the review UI offers. On a tie the parser wins for pattern
 *   fields (VIN, plate, km, cost, sizes) and the AI for meaning fields (brand, model,
 *   description, date, type, vehicle association).
 *
 * Maintenances and spare parts found by both are merged the same way; the rest are appended.
 * Ids are renumbered ("v0", "m0", "p0"...) and every vehicle reference follows its vehicle.
 */
fun mergeDrafts(local: ImportDraft, ai: ImportDraft): ImportDraft {
    val vehiclePairs = pairVehicles(local.vehicles, ai.vehicles)
    val newVehicleIds = mutableMapOf<String, String>()
    val vehicles = vehiclePairs.mapIndexed { index, (localVehicle, aiVehicle) ->
        val id = "v$index"
        localVehicle?.let { newVehicleIds[it.id] = id }
        aiVehicle?.let { newVehicleIds[it.id] = id }
        mergeVehicle(id, localVehicle, aiVehicle)
    }

    fun VehicleRef.remap(): VehicleRef =
        if (this is VehicleRef.Draft) newVehicleIds[draftId]?.let { VehicleRef.Draft(it) } ?: VehicleRef.Unassigned else this

    val maintenances = pairItems(
        local.maintenances.map { it.copy(vehicle = it.vehicle.remap()) },
        ai.maintenances.map { it.copy(vehicle = it.vehicle.remap()) },
        ::sameMaintenance,
    ).mapIndexed { index, (l, a) -> mergeMaintenance("m$index", l, a) }

    val spareParts = pairItems(
        local.spareParts.map { it.copy(vehicle = it.vehicle.remap()) },
        ai.spareParts.map { it.copy(vehicle = it.vehicle.remap()) },
        ::sameSparePart,
    ).mapIndexed { index, (l, a) -> mergeSparePart("p$index", l, a) }

    return ImportDraft(vehicles, maintenances, spareParts, aiApplied = local.aiApplied || ai.aiApplied)
}

private enum class Prefer { LOCAL, AI }

// --- Pairing -----------------------------------------------------------------------------------

/** AI vehicles in order (paired with a local one when possible), then unpaired local vehicles. */
private fun pairVehicles(local: List<VehicleDraft>, ai: List<VehicleDraft>): List<Pair<VehicleDraft?, VehicleDraft?>> {
    val remaining = local.toMutableList()
    val matchers: List<(VehicleDraft, VehicleDraft) -> Boolean> = listOf(
        { l, a -> sameValue(l.vin, a.vin) },
        { l, a -> sameValue(l.plate, a.plate) },
        { l, a -> textAgrees(l.brand?.value, a.brand?.value) && textAgrees(l.model?.value, a.model?.value) },
    )
    val pairs = ai.map { aiVehicle ->
        val match = matchers.firstNotNullOfOrNull { matches -> remaining.firstOrNull { matches(it, aiVehicle) } }
        match?.let { remaining.remove(it) }
        match to aiVehicle
    }
    return pairs + remaining.map { it to null }
}

private fun <T> pairItems(local: List<T>, ai: List<T>, same: (T, T) -> Boolean): List<Pair<T?, T?>> {
    val remaining = local.toMutableList()
    val pairs = ai.map { aiItem ->
        val match = remaining.firstOrNull { same(it, aiItem) }
        match?.let { remaining.remove(it) }
        match to aiItem
    }
    return pairs + remaining.map { it to null }
}

private fun compatibleVehicles(a: VehicleRef, b: VehicleRef): Boolean =
    a == b || a == VehicleRef.Unassigned || b == VehicleRef.Unassigned

private fun sameMaintenance(l: MaintenanceDraft, a: MaintenanceDraft): Boolean {
    if (!compatibleVehicles(l.vehicle, a.vehicle)) return false
    return sameValue(l.date, a.date) || sameValue(l.kilometers, a.kilometers) ||
        textAgrees(l.description?.value, a.description?.value, allowContainment = true)
}

private fun sameSparePart(l: SparePartDraft, a: SparePartDraft): Boolean {
    if (!compatibleVehicles(l.vehicle, a.vehicle)) return false
    return when {
        l.typeId != null && a.typeId != null -> l.typeId.value == a.typeId.value
        else -> sameValue(l.categoryId, a.categoryId) && textAgrees(l.brand?.value, a.brand?.value)
    }
}

// --- Merging -----------------------------------------------------------------------------------

private fun mergeVehicle(id: String, l: VehicleDraft?, a: VehicleDraft?): VehicleDraft = VehicleDraft(
    id = id,
    vin = merge(l?.vin, a?.vin, Prefer.LOCAL),
    plate = merge(l?.plate, a?.plate, Prefer.LOCAL),
    brand = mergeText(l?.brand, a?.brand, Prefer.AI),
    model = mergeText(l?.model, a?.model, Prefer.AI),
    year = merge(l?.year, a?.year, Prefer.AI),
    color = merge(l?.color, a?.color, Prefer.AI),
    owner = mergeText(l?.owner, a?.owner, Prefer.AI),
    vehicleType = merge(l?.vehicleType, a?.vehicleType, Prefer.AI),
    engineOil = mergeText(l?.engineOil, a?.engineOil, Prefer.LOCAL, allowContainment = true),
    frontTireSize = merge(l?.frontTireSize, a?.frontTireSize, Prefer.LOCAL),
    rearTireSize = merge(l?.rearTireSize, a?.rearTireSize, Prefer.LOCAL),
    recommendedFuel = merge(l?.recommendedFuel, a?.recommendedFuel, Prefer.AI),
    engineDetails = mergeText(l?.engineDetails, a?.engineDetails, Prefer.LOCAL, allowContainment = true),
    cylinderCount = merge(l?.cylinderCount, a?.cylinderCount, Prefer.LOCAL),
    frontBulbType = mergeText(l?.frontBulbType, a?.frontBulbType, Prefer.LOCAL, allowContainment = true),
    rearBulbType = mergeText(l?.rearBulbType, a?.rearBulbType, Prefer.LOCAL, allowContainment = true),
    batterySpecs = mergeText(l?.batterySpecs, a?.batterySpecs, Prefer.LOCAL, allowContainment = true),
    coolantType = mergeText(l?.coolantType, a?.coolantType, Prefer.LOCAL, allowContainment = true),
    frontTirePressureBelow160 = merge(l?.frontTirePressureBelow160, a?.frontTirePressureBelow160, Prefer.LOCAL),
    rearTirePressureBelow160 = merge(l?.rearTirePressureBelow160, a?.rearTirePressureBelow160, Prefer.LOCAL),
    frontTirePressureAbove160 = merge(l?.frontTirePressureAbove160, a?.frontTirePressureAbove160, Prefer.LOCAL),
    rearTirePressureAbove160 = merge(l?.rearTirePressureAbove160, a?.rearTirePressureAbove160, Prefer.LOCAL),
    firstRegistrationDate = merge(l?.firstRegistrationDate, a?.firstRegistrationDate, Prefer.LOCAL),
    nextItvDate = merge(l?.nextItvDate, a?.nextItvDate, Prefer.LOCAL),
    garageMatchVin = l?.garageMatchVin ?: a?.garageMatchVin,
)

private fun mergeMaintenance(id: String, l: MaintenanceDraft?, a: MaintenanceDraft?): MaintenanceDraft {
    val date = merge(l?.date, a?.date, Prefer.AI)
    // The precision follows whichever date won.
    val precision = when (date?.value) {
        null -> null
        a?.date?.value -> a?.datePrecision
        else -> l?.datePrecision
    }
    return MaintenanceDraft(
        id = id,
        description = mergeText(l?.description, a?.description, Prefer.AI, allowContainment = true),
        date = date,
        datePrecision = precision,
        rawDate = l?.rawDate ?: a?.rawDate,
        kilometers = merge(l?.kilometers, a?.kilometers, Prefer.LOCAL),
        cost = merge(l?.cost, a?.cost, Prefer.LOCAL),
        workshop = mergeText(l?.workshop, a?.workshop, Prefer.AI),
        mechanic = mergeText(l?.mechanic, a?.mechanic, Prefer.AI),
        vehicle = mergeVehicleRef(l?.vehicle, a?.vehicle),
    )
}

private fun mergeSparePart(id: String, l: SparePartDraft?, a: SparePartDraft?): SparePartDraft = SparePartDraft(
    id = id,
    phrase = l?.phrase ?: a?.phrase,
    categoryId = merge(l?.categoryId, a?.categoryId, Prefer.AI),
    typeId = merge(l?.typeId, a?.typeId, Prefer.AI),
    brand = mergeText(l?.brand, a?.brand, Prefer.AI),
    model = mergeText(l?.model, a?.model, Prefer.AI),
    reference = mergeText(l?.reference, a?.reference, Prefer.LOCAL),
    quantity = merge(l?.quantity, a?.quantity, Prefer.AI),
    unit = mergeText(l?.unit, a?.unit, Prefer.AI),
    vehicle = mergeVehicleRef(l?.vehicle, a?.vehicle),
)

/** The AI usually understands who a line refers to better, unless it left it unassigned. */
private fun mergeVehicleRef(local: VehicleRef?, ai: VehicleRef?): VehicleRef = when {
    ai != null && ai != VehicleRef.Unassigned -> ai
    local != null -> local
    else -> ai ?: VehicleRef.Unassigned
}

private fun <T> merge(
    local: SuggestedField<T>?,
    ai: SuggestedField<T>?,
    prefer: Prefer,
    agree: (T, T) -> Boolean = { x, y -> x == y },
    pickAgreed: (T, T) -> T = { x, y -> if (prefer == Prefer.LOCAL) x else y },
): SuggestedField<T>? {
    if (local == null) return ai
    if (ai == null) return local
    if (agree(local.value, ai.value)) {
        return SuggestedField(
            value = pickAgreed(local.value, ai.value),
            confidence = ExtractionConfidence.HIGH,
            source = FieldSource.BOTH,
            span = local.span,
        )
    }
    val localWins = when {
        local.confidence.ordinal < ai.confidence.ordinal -> true
        ai.confidence.ordinal < local.confidence.ordinal -> false
        else -> prefer == Prefer.LOCAL
    }
    return if (localWins) local.copy(alternative = ai.value) else ai.copy(alternative = local.value)
}

private fun mergeText(
    local: SuggestedField<String>?,
    ai: SuggestedField<String>?,
    prefer: Prefer,
    allowContainment: Boolean = false,
): SuggestedField<String>? = merge(
    local, ai, prefer,
    agree = { x, y -> textAgrees(x, y, allowContainment) },
    // When one contains the other, keep the more complete one.
    pickAgreed = { x, y ->
        when {
            comparable(x) == comparable(y) -> if (prefer == Prefer.LOCAL) x else y
            x.length >= y.length -> x
            else -> y
        }
    },
)

private fun comparable(text: String): String = foldForMatch(text).filter { it.isLetterOrDigit() }

private fun textAgrees(x: String?, y: String?, allowContainment: Boolean = false): Boolean {
    if (x == null || y == null) return false
    val a = comparable(x)
    val b = comparable(y)
    if (a.isEmpty() || b.isEmpty()) return false
    if (a == b) return true
    return allowContainment && minOf(a.length, b.length) >= 3 && (a.contains(b) || b.contains(a))
}

private fun <T> sameValue(x: SuggestedField<T>?, y: SuggestedField<T>?): Boolean =
    x != null && y != null && x.value == y.value
